/* ------------------------------------------------------------------ Field */
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);
  min-width: 0;
}

.label {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-snug);
}

.fieldDisabled .label {
  color: var(--color-text-muted);
}

.required {
  color: var(--color-danger-fg);
  font-weight: var(--font-weight-bold);
}

.optional {
  color: var(--color-text-subtle);
  font-weight: var(--font-weight-regular);
}

.hint {
  color: var(--color-text-subtle);
  font-size: var(--font-size-xs);
  line-height: var(--line-height-snug);
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
  color: var(--color-danger-fg);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-snug);
}

.error > svg {
  flex-shrink: 0;
  margin-top: 0.0625rem;
}

/* ------------------------------------------------------------------ Text-like controls */
.control {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: var(--layout-control-height-md);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  transition:
    border-color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.control:hover:not(.controlDisabled) {
  border-color: var(--color-border-hover);
}

.control:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary);
}

.control:has(:focus-visible) {
  outline: var(--layout-focus-ring-width) solid var(--color-focus-ring);
  outline-offset: 1px;
}

.control-sm {
  min-height: var(--layout-control-height-sm);
  font-size: var(--font-size-sm);
}

.control-lg {
  min-height: var(--layout-control-height-lg);
  font-size: var(--font-size-lg);
}

.controlInvalid,
.controlInvalid:hover:not(.controlDisabled) {
  border-color: var(--color-danger-fg);
}

.controlInvalid:focus-within {
  box-shadow: 0 0 0 1px var(--color-danger-fg);
}

.controlDisabled {
  background-color: var(--color-surface-sunken);
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

.controlReadOnly {
  background-color: var(--color-surface-sunken);
}

.input,
.select {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: inherit;
  padding: 0 var(--space-3);
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: inherit;
  font-size: inherit;
}

.input:focus-visible,
.select:focus-visible {
  outline: none;
}

.input::placeholder,
.textarea::placeholder {
  color: var(--color-text-subtle);
  opacity: 1;
}

.input:disabled,
.select:disabled {
  cursor: not-allowed;
}

.input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.adornment {
  display: inline-flex;
  align-items: center;
  padding-left: var(--space-3);
  color: var(--color-text-subtle);
  pointer-events: none;
}

.adornment + .input {
  padding-left: var(--space-2);
}

.trailing {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding-right: var(--space-1);
  color: var(--color-text-subtle);
}

.inlineButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
}

.inlineButton:hover:not(:disabled) {
  background-color: var(--color-surface-hover);
  color: var(--color-text);
}

.inlineButton:focus-visible {
  outline-offset: 0;
}

.textarea {
  display: block;
  width: 100%;
  min-height: 5rem;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  font-size: inherit;
  line-height: var(--line-height-normal);
  resize: vertical;
}

.textarea:hover:not(:disabled) {
  border-color: var(--color-border-hover);
}

.textarea:focus-visible {
  border-color: var(--color-primary);
  outline-offset: 1px;
}

.textarea:disabled {
  background-color: var(--color-surface-sunken);
  color: var(--color-text-disabled);
}

.textarea.controlInvalid {
  border-color: var(--color-danger-fg);
}

.noResize {
  resize: none;
}

.selectControl {
  padding-right: 0;
}

.select {
  appearance: none;
  padding-right: var(--space-8);
  cursor: pointer;
}

.select option {
  background-color: var(--color-surface);
  color: var(--color-text);
}

.selectChevron {
  position: absolute;
  right: var(--space-2-5);
  display: inline-flex;
  color: var(--color-text-muted);
  pointer-events: none;
}

/* ------------------------------------------------------------------ Checkbox & radio */
.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  padding: 0;
  border: 0;
}

.legend {
  padding: 0;
  margin-bottom: var(--space-1);
}

.choices {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.choicesRow {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
}

.choicesScroll {
  overflow-y: auto;
  padding: var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.choice {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-2);
  min-width: 0;
}

.choiceBare {
  gap: 0;
}

.choiceDisabled {
  opacity: 0.6;
}

.choiceText {
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5);
  min-width: 0;
}

.choiceLabel {
  color: var(--color-text);
  font-size: var(--font-size-md);
  line-height: 1.25rem;
  cursor: pointer;
}

.choiceDisabled .choiceLabel {
  cursor: not-allowed;
}

.choiceDescription {
  color: var(--color-text-subtle);
  font-size: var(--font-size-xs);
}

.checkWrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
}

.checkInput {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}

.checkInput:disabled {
  cursor: not-allowed;
}

.checkBox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  margin: 0.0625rem;
  border: 1.5px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface);
  color: var(--color-primary-contrast);
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

.checkMark {
  width: 0.875rem;
  height: 0.875rem;
  stroke: currentColor;
  opacity: 0;
}

.checkInput:hover:not(:disabled) + .checkBox {
  border-color: var(--color-primary);
}

.checkInput:checked + .checkBox,
.checkInput:indeterminate + .checkBox,
.checkMixed {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}

.checkInput:checked + .checkBox .checkMark,
.checkMixed .checkMark {
  opacity: 1;
}

.checkInput:focus-visible + .checkBox {
  outline: var(--layout-focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--layout-focus-ring-offset);
}

.checkInput[aria-invalid='true'] + .checkBox {
  border-color: var(--color-danger-fg);
}

.radioBox {
  border-radius: 50%;
}

.checkInput:checked + .radioBox {
  background-color: var(--color-surface);
  box-shadow: inset 0 0 0 0.25rem var(--color-primary);
}

.groupToolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
}

.groupSearch {
  flex: 1 1 12rem;
  width: auto;
}

.groupBulk {
  display: flex;
  gap: var(--space-3);
}

.textButton {
  color: var(--color-link);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.textButton:hover {
  color: var(--color-link-hover);
  text-decoration: underline;
}

.groupCount {
  color: var(--color-text-subtle);
  font-size: var(--font-size-xs);
}

/* ------------------------------------------------------------------ Switch */
.switchRow {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.switchRowStart {
  flex-direction: row-reverse;
  justify-content: space-between;
}

.switch {
  --switch-w: 2.25rem;
  --switch-h: 1.25rem;
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: var(--switch-w);
  height: var(--switch-h);
  border-radius: var(--radius-full);
  border: 1.5px solid var(--color-border-strong);
  background-color: var(--color-surface-sunken);
  transition:
    background-color var(--duration-normal) var(--ease-out),
    border-color var(--duration-normal) var(--ease-out);
}

.switch-sm {
  --switch-w: 1.875rem;
  --switch-h: 1.0625rem;
}

.switchThumb {
  position: absolute;
  top: 50%;
  left: 2px;
  width: calc(var(--switch-h) - 7px);
  height: calc(var(--switch-h) - 7px);
  border-radius: 50%;
  background-color: var(--color-text-muted);
  transform: translateY(-50%);
  transition:
    transform var(--duration-normal) var(--ease-out),
    background-color var(--duration-normal) var(--ease-out);
}

.switch[aria-checked='true'] {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}

.switch[aria-checked='true'] .switchThumb {
  background-color: var(--color-primary-contrast);
  transform: translate(calc(var(--switch-w) - var(--switch-h)), -50%);
}

.switch:disabled {
  opacity: 0.55;
}

/* ------------------------------------------------------------------ Layout */
.section {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  padding-bottom: var(--space-6);
  border-bottom: 1px solid var(--color-border);
}

.section:last-of-type {
  border-bottom: 0;
}

.sectionHeader {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.sectionTitle {
  font-size: var(--font-size-lg);
}

.sectionDescription {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  max-width: 48rem;
}

.sectionGrid {
  display: grid;
  gap: var(--space-4) var(--space-5);
  grid-template-columns: minmax(0, 1fr);
}

@media (min-width: 768px) {
  .cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.actions-start {
  justify-content: flex-start;
}

.actions-end {
  justify-content: flex-end;
}

.actions-between {
  justify-content: space-between;
}

.actionsSticky {
  position: sticky;
  bottom: 0;
  z-index: var(--z-raised);
  padding: var(--space-3) 0;
  border-top: 1px solid var(--color-border);
  background-color: var(--color-surface);
}

@media (max-width: 480px) {
  .actions-end,
  .actions-between {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}
